import React, { Fragment, ReactElement, UIEventHandler, useState } from 'react'
import { ResizableBox } from 'react-resizable'
import 'react-resizable/css/styles.css'
import { TableVirtuoso } from 'react-virtuoso'
import { Box, TableRow, TableCell, Checkbox } from '@mui/material'
import { Column } from './types'
import { TableToolbar } from './TableToolbar'
import {
  MuiTableWrapper,
  PlainTableBodyWrapper,
  PlainTableHeadWrapper,
} from './components'
import { CSVExportButton } from './CSVExportButton'

export interface TableProps<T> {
  title?: React.ReactNode
  columns: Column<T>[]
  items: T[]
  footer?: ReactElement
  onRowClick?: (index: number) => void
  onRowSelect?: (index: number) => void
  selectedRows?: number[]
  toolBarItems?: ReactElement[]
  overrideHeight?: number
  scrollDisabled?: boolean
  canExport?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRowStyle?: (index: number) => Record<string, any> | undefined
}

const cellSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

export function Table<T>({
  title = null,
  items,
  columns,
  footer,
  onRowClick,
  onRowSelect,
  selectedRows = [],
  toolBarItems = [],
  overrideHeight,
  scrollDisabled,
  canExport,
  getRowStyle,
}: TableProps<T>) {
  const [scrollPosition, setScrollPosition] = useState(0)
  const barItems = [...toolBarItems]
  if (canExport) {
    barItems.push(
      <CSVExportButton
        title={title ? title.toString() : 'TableExport'}
        key="csvExport"
        columnInfo={columns}
        columnData={items}
      />,
    )
  }

  const onScroll: UIEventHandler<HTMLDivElement> = (e) => {
    if (scrollDisabled) {
      ;(e.target as HTMLElement).scrollTo({
        top: scrollPosition,
        left: 0,
      })
    } else {
      setScrollPosition((e.target as HTMLElement).scrollTop)
    }
  }

  const header = () => {
    const numSelected = selectedRows.length
    const rowCount = items.length
    return (
      <TableRow sx={{ bgcolor: 'white' }}>
        {onRowSelect && (
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onClick={(e) => {
                onRowSelect(-1)
                e.stopPropagation()
                e.preventDefault()
                return false
              }}
            />
          </TableCell>
        )}
        {columns.map(({ title, width }, index) => (
          <TableCell key={`${title}-${index}-header`} width={width} sx={cellSx}>
            {title}
          </TableCell>
        ))}
      </TableRow>
    )
  }
  const aRow = (rowIndex: number) => {
    const data = items[rowIndex]
    return (
      <Fragment key={`row-index-${rowIndex}`}>
        {onRowSelect && (
          <TableCell padding="checkbox">
            <Checkbox
              checked={selectedRows.includes(rowIndex)}
              color="primary"
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                onRowSelect(rowIndex)
                return false
              }}
            />
          </TableCell>
        )}
        {columns.map(({ title, rowContent }, colIndex) => (
          <TableCell align="left" key={`${title}-${colIndex}-row`} sx={cellSx}>
            {rowContent(data, { rowIndex, colIndex })}
          </TableCell>
        ))}
      </Fragment>
    )
  }

  return (
    <ResizableBox
      style={{
        border: 'solid 1px lightGrey',
        borderRadius: '5px',
        background: 'white',
      }}
      axis="y"
      height={overrideHeight ?? 600}
    >
      <Box height="100%" display="flex" flexDirection="column">
        {(title || onRowSelect || !!barItems.length) && (
          <TableToolbar
            title={title ?? ''}
            numSelected={selectedRows?.length ?? 0}
            toolBarItems={barItems}
          />
        )}
        <TableVirtuoso
          onScroll={onScroll}
          totalCount={items.length}
          itemContent={aRow}
          fixedHeaderContent={header}
          style={{
            width: '100%',
          }}
          components={{
            Table: MuiTableWrapper,
            TableBody: PlainTableBodyWrapper,
            TableHead: PlainTableHeadWrapper,
            TableRow: tableRowWithRowClick(onRowClick, getRowStyle),
          }}
        ></TableVirtuoso>
        {footer}
      </Box>
    </ResizableBox>
  )
}

function tableRowWithRowClick(
  onRowClick?: (index: number) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRowStyle?: (index: number) => Record<string, any> | undefined,
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function TableRowWrapper(props: any) {
    const rowIndex = props['data-item-index']
    const rowStyle = getRowStyle ? getRowStyle(rowIndex) : undefined

    return (
      <TableRow
        {...props}
        sx={{
          '&:hover': { cursor: onRowClick ? 'pointer' : 'inherit' },
          borderBottom: 'solid 1px lightGrey',
          ...(rowStyle || {}),
        }}
        hover={!!onRowClick}
        role="checkbox"
        onClick={() => onRowClick?.(props['data-item-index'])}
      />
    )
  }
  return TableRowWrapper
}
